import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { blogPosts, BlogSection } from './blog-data';

function renderSection(section: BlogSection, index: number) {
	switch (section.type) {
		case 'heading':
			return (
				<h2 key={index} className="font-playfair-custom-bold text-2xl lg:text-3xl text-gray-900 mt-10 mb-4">
					{section.text}
				</h2>
			);
		case 'paragraph':
			return (
				<p key={index} className="text-gray-600 leading-relaxed font-montserrat-custom-medium mb-5 text-[1.05rem]">
					{section.text}
				</p>
			);
		case 'list':
			return (
				<ul key={index} className="mb-6 space-y-3">
					{section.items?.map((item, i) => (
						<li key={i} className="flex items-start gap-3 text-gray-600 font-montserrat-custom-medium text-[1.05rem]">
							<span className="mt-1.5 flex-shrink-0 w-2 h-2 rounded-full bg-primary-500" />
							{item}
						</li>
					))}
				</ul>
			);
		case 'quote':
			return (
				<blockquote key={index} className="my-8 border-l-4 border-primary-400 pl-6 py-2 bg-primary-50 rounded-r-2xl">
					<p className="text-gray-800 font-montserrat-custom-medium italic leading-relaxed text-lg">
						„{section.text}"
					</p>
					<p className="mt-2 text-sm text-primary-600 font-bold">— Amelia Pałczyńska</p>
				</blockquote>
			);
		default:
			return null;
	}
}

export default function BlogPostPage() {
	const { slug } = useParams<{ slug: string }>();
	const post = blogPosts.find((p) => p.slug === slug);

	if (!post) {
		return <Navigate to="/blog" replace />;
	}

	const currentIndex = blogPosts.findIndex((p) => p.slug === slug);
	const prevPost = currentIndex > 0 ? blogPosts[currentIndex - 1] : null;
	const nextPost = currentIndex < blogPosts.length - 1 ? blogPosts[currentIndex + 1] : null;

	return (
		<div className="pt-20 lg:pt-24 pb-20">
			<div className={`relative bg-gradient-to-br ${post.gradient} py-20 lg:py-28 overflow-hidden`}>
				<div className="absolute top-0 right-0 text-white/10 font-playfair-custom-bold text-[200px] leading-none select-none translate-x-8 -translate-y-8">
					{post.number}
				</div>
				<div className="relative max-w-4xl mx-auto px-6">
					<Link
						to="/blog"
						className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm font-montserrat-custom-medium transition-colors mb-8"
					>
						<ArrowBackIcon sx={{ fontSize: 18 }} />
						Wróć do bloga
					</Link>
					<span className="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-xs font-bold rounded-full mb-4 border border-white/20">
						{post.category}
					</span>
					<h1 className="font-playfair-custom-bold text-4xl lg:text-5xl text-white leading-tight mb-6 max-w-2xl">
						{post.title}
					</h1>
					<div className="flex items-center gap-4 text-white/70 text-sm font-montserrat-custom-medium">
						<div className="flex items-center gap-1.5">
							<AccessTimeIcon sx={{ fontSize: 16 }} />
							{post.readTime} czytania
						</div>
						<span>·</span>
						<span>{post.date}</span>
					</div>
				</div>
			</div>

			<div className="max-w-3xl mx-auto px-6 mt-12">
				<p className="text-gray-500 text-lg leading-relaxed font-montserrat-custom-medium italic border-b border-gray-100 pb-8 mb-8">
					{post.excerpt}
				</p>

				<div>
					{post.content.map((section, index) => renderSection(section, index))}
				</div>

				<div className="mt-16 pt-10 border-t border-gray-100">
					<div className="bg-gradient-to-br from-primary-50 to-white rounded-3xl p-8 flex flex-col sm:flex-row items-center gap-6 shadow-soft">
						<img
							src="/photo_6592.jpg"
							alt="Amelia Pałczyńska"
							className="w-20 h-20 rounded-full object-cover flex-shrink-0 shadow-soft"
						/>
						<div className="text-center sm:text-left">
							<p className="font-bold text-gray-900 font-montserrat-custom-medium">Amelia Pałczyńska</p>
							<p className="text-sm text-gray-500 font-montserrat-custom-medium mt-1">
								Trenerka personalna specjalizująca się w treningu siłowym dla kobiet. Rzeszów.
							</p>
						</div>
						<div className="sm:ml-auto flex-shrink-0">
							<Link
								to="/contact"
								className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-500 to-primary-600 text-white font-bold text-sm rounded-full shadow-soft-lg hover:scale-105 transition-all duration-300"
							>
								Umów trening
								<ArrowForwardIcon sx={{ fontSize: 16 }} />
							</Link>
						</div>
					</div>
				</div>

				{(prevPost || nextPost) && (
					<div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
						{prevPost && (
							<Link
								to={`/blog/${prevPost.slug}`}
								className="group flex flex-col gap-1 bg-white rounded-2xl p-5 shadow-soft hover:shadow-soft-lg transition-all duration-300 border border-gray-100"
							>
								<span className="text-xs text-gray-400 font-montserrat-custom-medium">← Poprzedni artykuł</span>
								<span className="text-gray-800 font-bold group-hover:text-primary-600 transition-colors line-clamp-2">
									{prevPost.title}
								</span>
							</Link>
						)}
						{nextPost && (
							<Link
								to={`/blog/${nextPost.slug}`}
								className="group flex flex-col gap-1 bg-white rounded-2xl p-5 shadow-soft hover:shadow-soft-lg transition-all duration-300 border border-gray-100 sm:text-right"
							>
								<span className="text-xs text-gray-400 font-montserrat-custom-medium">Następny artykuł →</span>
								<span className="text-gray-800 font-bold group-hover:text-primary-600 transition-colors line-clamp-2">
									{nextPost.title}
								</span>
							</Link>
						)}
					</div>
				)}
			</div>
		</div>
	);
}
